import { liveQuerySubscribeLock, LIVE_QUERY_SUBSCRIBE_LOCK_KEY } from 'util/asyncLocks';
import * as actions from 'actions';
import * as tabs from 'constants/tabNames';
import {
  doesAssignedExpertHaveLastReplyToSelectedConversation,
  isSpecialist,
  extractNonHistoricalTicketIdFromState,
  extractSelectedInboxTab,
  getTicketRemovalInProcess,
  extractAssignedChats,
  extractAssignedEmails,
  getChatAlertActiveId,
  getPartnersAllowlist,
} from 'extractFromState';
import { getExpertId } from 'util/parseSelectors';
import { isLiveChat } from 'util/chat';
import {
  CONVERSATION_CREATE_SUBSCRIPTION,
  CONVERSATION_UPDATE_SUBSCRIPTION,
  CONVERSATION_ENTER_SUBSCRIPTION,
  CONVERSATION_LEAVE_SUBSCRIPTION,
} from './conversationSubscriptions.gql';
import { chooseDefaultTicket } from 'util/chooseDefault';
import subscribeToLiveQuery from '../subscribeToLiveQuery';
import { updateTicketInQueue } from 'actions/ticketActions';
import { clearInProgressTreeAndWizardLocation } from 'actions/inProgressTreeAndWizardLocationActions';
import { oneBtnPopupCreator, getUnassignmentPopupConfig } from 'components/popups/ConfirmPopup/helper';
import { deactivateHistoricalTicket } from 'actions/tools/ToolActions';
import { TICKET_STATUS_ASSIGNED } from 'constants/ticketStatuses';
import { extractAssignedTickets } from '../../extractFromState';
import isEmpty from 'lodash/isEmpty';

const assignedQueueSubscriptions = [];

// This is mainly for unit tests
const resetAssignedQueueSubscriptions = () => {
  // Remove all elements from validateIntentTicketSubscriptions
  assignedQueueSubscriptions.length = 0;
};

const startAssignedLiveQuery = (store) => {
  /*
   * Starting Assigned Ticket Queue(My Items) live query.
   *
   * Shouldn't start live query if it has already been started.
   */

  if (!isEmpty(assignedQueueSubscriptions)) {
    return;
  }

  liveQuerySubscribeLock.acquire(LIVE_QUERY_SUBSCRIBE_LOCK_KEY, async (lockDoneFunction) => {
    try {
      const expertId = getExpertId();

      if (!expertId) {
        return;
      }

      const partnersAllowlist = getPartnersAllowlist(store.getState());

      const filter = {
        simplrExpertId: {
          eq: expertId,
        },
        status: {
          eq: TICKET_STATUS_ASSIGNED,
        },
        isOpen: {
          is: true,
        },
      };

      if (!isEmpty(partnersAllowlist)) {
        filter.and = [{ simplrAccountId: { in: [...partnersAllowlist] } }];
      }

      const variables = {
        filter,
      };

      const handleNewAssignedConversation = async (conversationObj) => {
        if (conversationObj.isOpen) {
          let state = store.getState();
          let assignedTickets = state.tickets.assignedTickets;
          if (!assignedTickets[conversationObj.objectId]) {
            await store.dispatch(
              actions.addTicketToQueue({
                conversation: conversationObj,
                queueName: tabs.ASSIGNED,
              })
            );
            state = store.getState(); // get updated state
            assignedTickets = state.tickets.assignedTickets;
            const selectedInboxTab = extractSelectedInboxTab(state);
            const isIncomingChat = isLiveChat(conversationObj.communicationType);
            const ticketInAssignedQueue = assignedTickets[assignedTickets.allTickets[0]];
            const isTicketInQueueChat = isLiveChat(ticketInAssignedQueue.communicationType);
            const numAssignedConversations = assignedTickets.allTickets.length;
            const numAssignedChats = extractAssignedChats(state).length;
            const numAssignedEmails = extractAssignedEmails(state).length;

            // only select if you are on this tab and only one ticket exists in tab
            if (isSpecialist(state)) {
              if (selectedInboxTab === tabs.ASSIGNED && numAssignedConversations === 1) {
                store.dispatch(actions.selectTicket(assignedTickets.allTickets[0], tabs.ASSIGNED));
              }
            } else {
              let newSelectedTicketId;
              if (
                selectedInboxTab === tabs.LIVE_CHAT &&
                isIncomingChat &&
                isTicketInQueueChat &&
                numAssignedChats === 1
              ) {
                newSelectedTicketId = chooseDefaultTicket(tabs.LIVE_CHAT, state);
                store.dispatch(actions.selectTicket(newSelectedTicketId, tabs.LIVE_CHAT));
              } else if (
                selectedInboxTab === tabs.ASSIGNED &&
                !isIncomingChat &&
                !isTicketInQueueChat &&
                numAssignedEmails === 1
              ) {
                newSelectedTicketId = chooseDefaultTicket(tabs.ASSIGNED, state);
                store.dispatch(actions.selectTicket(newSelectedTicketId, tabs.ASSIGNED));
              }
            }
          }
        }
      };

      const handleOnUpdateConversation = async (conversationObj) => {
        const assignedTickets = extractAssignedTickets(store.getState());
        const existingTicket = assignedTickets[conversationObj.objectId];

        if (!existingTicket) {
          // we need to make sure that the conversation is on state to avoid the ghost notification bug. Ticket SPD-9106
          await store.dispatch(
            actions.addTicketToQueue({
              conversation: conversationObj,
              queueName: tabs.ASSIGNED,
            })
          );
        }
        /*
         *  This event will be triggered when an existing assigned ticket is updated.
         *  Used to update the Unread Count on the UI.
         */
        store.dispatch(updateTicketInQueue(conversationObj, tabs.ASSIGNED, store.getState().user.isAdmin));
      };

      const handleOnConversationLeave = async (conversationObj) => {
        /*
         *  This event will be triggered when an existing assigned ticket's status changes,
         *  usually via "Resolve" & "Escalate" functionality
         */
        let assignedTickets = store.getState().tickets.assignedTickets;
        const existingTicket = assignedTickets[conversationObj.objectId];
        const tabName = store.getState().ui.messages.selectedInboxTab;

        // clear wizard location state even if ticket has been removed to avoid state overload
        store.dispatch(clearInProgressTreeAndWizardLocation(conversationObj.objectId));

        if (existingTicket) {
          // Will be parent ticket ID if historical ticket is selected
          const selectedTicketId = extractNonHistoricalTicketIdFromState(store.getState());

          store.dispatch(
            actions.removeTicketFromQueue(conversationObj.objectId, 'assigned', store.getState().user.isAdmin)
          );

          const chatAlertActiveId = getChatAlertActiveId(store.getState());
          if (chatAlertActiveId === conversationObj.objectId) {
            // if ticket had an open alert notification, close it
            store.dispatch(actions.setChatAlertActiveId(null));
          }

          if (selectedTicketId === conversationObj.objectId) {
            const ticketRemovalInProgress = getTicketRemovalInProcess(store.getState());
            const doesCurrentExpertHaveLastReply = doesAssignedExpertHaveLastReplyToSelectedConversation(
              store.getState()
            );
            const popupConfig = getUnassignmentPopupConfig(conversationObj, doesCurrentExpertHaveLastReply);
            // do not show popup if change is in progress for ticket
            // show a popup if we have a config for this conversation
            if (!ticketRemovalInProgress && popupConfig) {
              oneBtnPopupCreator(popupConfig);
            }

            // deactivate the historical one to keep consistent removing leaving the queue behavior
            store.dispatch(deactivateHistoricalTicket());

            // TODO this logic is already inside selectTicket - all of this can be removed
            const ticketId = chooseDefaultTicket(tabName, store.getState());
            // TODO clean state if selectTicket not selected
            if (ticketId) {
              store.dispatch(actions.selectTicket(ticketId, tabName));
              const toolsCurrentlyOpen = store.getState().ui.tools.general.showTools;
              if (toolsCurrentlyOpen) {
                store.dispatch(actions.setShowTools(false));
              }
            }
          }
        }
      };

      const createSubscription = subscribeToLiveQuery(
        CONVERSATION_CREATE_SUBSCRIPTION,
        variables,
        'onCreateConversation',
        handleNewAssignedConversation
      );

      const updateSubscription = subscribeToLiveQuery(
        CONVERSATION_UPDATE_SUBSCRIPTION,
        variables,
        'onUpdateConversation',
        handleOnUpdateConversation
      );

      const enterSubscription = subscribeToLiveQuery(
        CONVERSATION_ENTER_SUBSCRIPTION,
        variables,
        'onEnterConversation',
        handleNewAssignedConversation
      );

      const leaveSubscription = subscribeToLiveQuery(
        CONVERSATION_LEAVE_SUBSCRIPTION,
        variables,
        'onLeaveConversation',
        handleOnConversationLeave
      );

      assignedQueueSubscriptions.push(createSubscription, updateSubscription, enterSubscription, leaveSubscription);
    } finally {
      lockDoneFunction();
    }
  });
};

export { startAssignedLiveQuery, resetAssignedQueueSubscriptions };
